// Catalog.ID Client — the organization wallet after the org bag is finalized
// ============================================================================
// apps/bitcash/docs/plans/bitcash-v2.md M12, §4.13, D14. `finalizeCredentials` writes the org bag
// as a NEW object with the organization wallet's entry appended (its code is
// minted by the finalize ceremony, after identity generation wrapped the org
// keys that existed then). Two things must follow it:
//
//   * the wizard's `shared['orgKeyBag']` becomes the finalized bag — the org
//     kit prints the wallet's code from it, the org wallet's only printed backup;
//   * the founding delegate (owner, entitled to `bitcash_wallet`, D14) gets its
//     wrap of the new key, which `getOrgWalletKey` reads. The wrap goes through
//     the roles route, which replaces a delegate's wraps wholesale: the
//     founder's existing wraps are re-sent as they are, plus the new one.
//
// Idempotent: a resume whose founder already holds the wrap uploads nothing.

import type { KeyBag } from '../crypto/keybag';
import { BITCASH_WALLET_KEY_ID, activeBitcashWalletEntries, decodeBitcashWalletEntry } from '../crypto/bitcash-wallet-entry';
import { getKeyBagEntry } from '../crypto/keybag';

/** The organization wallet's code as a bag holds it, or null. */
export function walletKeyFromBag(bag: KeyBag | undefined | null): string | null {
    if (!bag) return null;
    const entry = getKeyBagEntry(bag, BITCASH_WALLET_KEY_ID);
    return entry ? decodeBitcashWalletEntry(entry.privateKey).key : null;
}

export interface WrappedOrgKey { keyId: string; keyIndex: number; wrappedKey: string }

export interface OrgWalletCustodyDeps {
    /** The session delegate's own wrapped org keys (`GET /api/delegate/wrappedkeys`). */
    getWrappedOrgKeys(): Promise<WrappedOrgKey[]>;
    /** `PUT /api/organization/{orgCid}/delegates/{delegateCid}/roles`: given wraps replace the delegate's wholesale. */
    updateRoles(orgCid: string, delegateCid: string, req: { roles: string[]; wrappedOrgKeys: WrappedOrgKey[] }): Promise<void>;
    /** `crypto/claims.ts` wrapPrivateKey. */
    wrap(privateKeyBase64: string, recipientPublicKey: Uint8Array): Promise<string>;
}

export async function adoptFinalizedOrgBag(
    args: {
        shared: Record<string, unknown>;
        orgCid: string;
        founderCid: string;
        /** The founding delegate's `attr_encrypt` public key. */
        founderAttrPublicKey: Uint8Array;
        finalizedBag: KeyBag;
    },
    deps: OrgWalletCustodyDeps,
): Promise<void> {
    args.shared['orgKeyBag'] = args.finalizedBag;

    const wallet = activeBitcashWalletEntries(args.finalizedBag)[0];
    if (wallet === undefined) return;
    const keyIndex = wallet.keyIndex ?? 0;
    const own = await deps.getWrappedOrgKeys();
    if (own.some(w => w.keyId === BITCASH_WALLET_KEY_ID && w.keyIndex === keyIndex)) return;
    const wrappedKey = await deps.wrap(wallet.privateKey, args.founderAttrPublicKey);
    // The founder of a new organization is its owner (OrganizationController's registration).
    await deps.updateRoles(args.orgCid, args.founderCid, {
        roles: ['owner'],
        wrappedOrgKeys: [...own, { keyId: BITCASH_WALLET_KEY_ID, keyIndex, wrappedKey }],
    });
}
